/**
 * Contextual tutorials catalogue. Each entry is shown ONCE (per game) the
 * first time the player encounters the mechanic — to fill the gaps left
 * by the linear onboarding tour. Triggers are wired from the relevant
 * slices (eventSlice, taxSlice, coreSlice…) via `triggerContextualTuto`.
 *
 * Authoring guidelines (28/05/2026) :
 *   - Sobre/pro : 1-2 paragraphes courts, pas de mentor incarné.
 *   - "Why it matters" + "what to look at" + "what NOT to do".
 *   - `cta`           : libellé du bouton "OK compris".
 *   - `targetTestid`  : optional, scrolls to + focuses an element when shown.
 *   - `route`         : optional, suggested route the player should be on.
 */
export const CONTEXTUAL_TUTORIALS = {
  // ── Aléas / événements ──────────────────────────────────────────────
  firstEvent: {
    id: 'firstEvent',
    title: 'Un aléa vient de se déclencher',
    body: [
      "Les aléas sont au cœur du jeu. Chaque choix a un **coût immédiat** (trésorerie) ET une **conséquence narrative** : réputation, satisfaction des chauffeurs, ou opportunité saisie.",
      "**À retenir :** ne cherchez pas le choix « gratuit ». Le moins coûteux à court terme est souvent le plus risqué pour votre réputation. Lisez chaque option avant de cliquer.",
    ],
    cta: "Je vais lire les options",
  },

  // ── TVA / fiscalité ─────────────────────────────────────────────────
  firstTaxDue: {
    id: 'firstTaxDue',
    title: 'Première déclaration de TVA',
    body: [
      "La TVA collectée sur vos factures n'est **pas votre argent** — vous la reversez à l'État. La TVA déductible (sur vos charges) vient s'y soustraire.",
      "**TVA nette = collectée − déductible.** Positive : à payer. Négative : crédit reporté.",
      "Si votre trésorerie est insuffisante, demandez un **échéancier DGFiP** (6 mensualités). Cette option ne sera offerte qu'une fois par an.",
    ],
    cta: "Compris",
    route: '/fiscalite',
  },

  // ── Crédit bancaire ─────────────────────────────────────────────────
  firstCreditOffer: {
    id: 'firstCreditOffer',
    title: 'Crédit bancaire — vous y avez désormais accès',
    body: [
      "Vous avez **3 mois d'historique d'exploitation** : les banques acceptent maintenant d'instruire votre dossier. C'est le délai réglementaire avant qu'un crédit professionnel ne devienne accessible.",
      "Un crédit transforme un investissement lourd en mensualités lissées sur 12 à 60 mois. Vous gardez de la trésorerie pour absorber les aléas.",
      "**Attention :** une mensualité s'ajoute à vos charges fixes pendant toute la durée. Vérifiez que votre **marge mensuelle** absorbe la dette sans devenir négative.",
      "Le coût total = capital + intérêts. Plus la durée est longue, plus vous payez d'intérêts.",
    ],
    cta: "Bien noté",
    route: '/finance',
  },

  // ── Bascule de phase ────────────────────────────────────────────────
  firstPhaseUp: {
    id: 'firstPhaseUp',
    title: 'Vous changez de phase de jeu',
    body: [
      "Chaque phase débloque **de nouvelles mécaniques** : véhicules premium, contrats long-terme, statuts juridiques avancés, M&A…",
      "Allez consulter le manuel pédagogique (📖 bouton en bas à droite) pour découvrir ce qui est désormais accessible. Une nouvelle phase = un nouveau niveau de complexité, profitez-en mais restez prudent.",
    ],
    cta: "Compris",
  },

  // ── Chauffeur insatisfait ───────────────────────────────────────────
  firstUnhappyDriver: {
    id: 'firstUnhappyDriver',
    title: 'Un chauffeur perd patience',
    body: [
      "Quand la **satisfaction d'un chauffeur passe sous 45 %**, il commence à envisager de démissionner. Sous 20 %, le risque devient quotidien.",
      "Leviers à votre disposition :",
      "• **Bonus ponctuel** (150 €) : +15 satisfaction",
      "• **Repos forcé** : réduit la fatigue, fait remonter la satisfaction",
      "• **Reconnaître son trait de personnalité** : assignez-lui des missions qu'il/elle apprécie (cf. l'onglet RH)",
    ],
    cta: "Je gère",
    route: '/hr',
  },

  // ── M&A ────────────────────────────────────────────────────────────
  firstMA: {
    id: 'firstMA',
    title: 'Une opportunité d\'acquisition',
    body: [
      "L'achat d'un concurrent (M&A) est une **opération à très fort levier** : flotte, contrats, clientèle et chauffeurs basculent d'un coup chez vous.",
      "**Risques :** coût d'acquisition souvent élevé (3-5 × EBITDA), choc culturel (chauffeurs peuvent partir), dette d'intégration.",
      "**Avant de signer :** demandez un rapport d'audit à votre Daf — il analysera la cible et estimera la rentabilité de l'opération sur 24 mois.",
    ],
    cta: "Compris",
  },

  // ── Retard de paiement client ───────────────────────────────────────
  firstLatePayment: {
    id: 'firstLatePayment',
    title: 'Un client paye en retard',
    body: [
      "Vos clients sont notés sur 100 (palier **fidélité**). Un retard de paiement vous pénalise sur votre **trésorerie** (le revenu n'arrive pas dans les comptes) mais surtout signale un problème dans la relation.",
      "**Réflexe :** consultez l'onglet Clients pour identifier les clients à risque. Un client « Méfiant » accumule les retards — mieux vaut sécuriser un acompte ou abandonner le contrat avant qu'il dégrade votre BFR.",
    ],
    cta: "Je vais voir",
    route: '/clients',
  },

  // ── 18/06/2026 — Évolutions récentes du jeu ─────────────────────────
  firstTollContract: {
    id: 'firstTollContract',
    title: 'Péages autoroutiers — comptez-les dans votre marge',
    body: [
      "Ce contrat parcourt plus de 30 km : des **péages** s'appliquent — 0,08 €/km par jour et par véhicule, plafonnés à **50 €/jour** (×1,5 pour les convois exceptionnels Tier 5).",
      "**À retenir :** le revenu/jour affiché est BRUT. La carte contrat détaille le péage estimé — la vraie marge, c'est revenu − charges − péages. Un contrat lointain peut rapporter moins qu'un contrat proche de votre dépôt.",
    ],
    cta: "Je vérifie mes marges",
  },

  firstInterim: {
    id: 'firstInterim',
    title: 'Intérim — la mission continue sans votre chauffeur',
    body: [
      "Vous venez d'activer un **intérimaire** : votre chauffeur est bien en arrêt, mais son véhicule et son contrat **continuent de tourner** — aucun revenu perdu pendant l'absence.",
      "**Comment ça marche :** le coût de l'intérim est débité une fois, le titulaire retrouve automatiquement sa mission à son retour. Ce mécanisme existe aussi pour les **congés** (choix « Accorder + sous-traiter » dans la messagerie).",
    ],
    cta: "Compris",
  },

  firstTvaCredit: {
    id: 'firstTvaCredit',
    title: 'Crédit de TVA — quand l\'État vous doit de l\'argent',
    body: [
      "Vos achats récents (véhicule, investissements) ont généré plus de **TVA déductible** que vous n'avez collecté de TVA sur vos ventes. Résultat : un **crédit de TVA**.",
      "**Concrètement :** à la prochaine déclaration, ce crédit s'impute sur ce que vous devez — voire se reporte sur les périodes suivantes. C'est l'un des mécanismes qui rendent l'investissement moins coûteux qu'il n'y paraît en trésorerie.",
    ],
    cta: "Bien noté",
    route: '/fiscalite',
  },

  firstMarketScaleUp: {
    id: 'firstMarketScaleUp',
    title: 'Le marché s\'adapte à votre croissance',
    body: [
      "Votre entreprise a pris de l'ampleur — et le marché l'a remarqué. Les **concurrents grossissent avec vous** (CA, flotte, effectifs) et les **Appels d'Offres** deviennent plus rémunérateurs.",
      "**Conséquences :** les rachats de concurrents coûtent plus cher qu'avant, mais les AO récompensent mieux votre taille. Surveillez l'onglet Marché pour suivre l'évolution du rapport de force.",
    ],
    cta: "Compris",
    route: '/marche',
  },
};
